import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import venueListsApiService from '../../services/venueListsApi';
import './VenueLists.css';

const VenueListInviteAccept = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | needs-auth | success | error
  const [message, setMessage] = useState('');

  const inviteToken = useMemo(
    () => searchParams.get('invite_token') || searchParams.get('token') || '',
    [searchParams]
  );
  const listIdFromQuery = useMemo(() => searchParams.get('list_id') || '', [searchParams]);

  useEffect(() => {
    const acceptInvite = async () => {
      if (!inviteToken) {
        setStatus('error');
        setMessage('Invalid invite link. Missing invite token.');
        return;
      }

      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) {
        setStatus('needs-auth');
        setMessage('Please sign in with the invited email address to accept this invite.');
        return;
      }

      try {
        setLoading(true);
        setStatus('idle');
        setMessage('');
        const result = await venueListsApiService.acceptInvite(inviteToken);
        const acceptedListId =
          listIdFromQuery ||
          result?.list_id ||
          result?.venue_list_id ||
          result?.venue_list?.id ||
          '';
        setStatus('success');
        setMessage('Invite accepted successfully.');
        if (acceptedListId) {
          setTimeout(() => {
            navigate(`/venue-lists/${acceptedListId}`);
          }, 350);
        }
      } catch (error) {
        if (error?.message === 'SESSION_EXPIRED') {
          setStatus('needs-auth');
          setMessage('Session expired. Please sign in again to accept this invite.');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          return;
        }
        setStatus('error');
        setMessage(error?.message || 'Failed to accept invite.');
      } finally {
        setLoading(false);
      }
    };

    acceptInvite();
  }, [inviteToken, listIdFromQuery, navigate]);

  return (
    <section className="venue-lists-page">
      <div className="venue-lists-shell">
        <div className="venue-lists-card venue-lists-accept-card">
          <h1>Venue list invite</h1>
          {loading ? <p className="venue-lists-note">Accepting your invite...</p> : null}
          {!loading && message ? (
            <p
              className={
                status === 'success'
                  ? 'venue-lists-success'
                  : status === 'needs-auth'
                    ? 'venue-lists-note'
                    : 'venue-lists-error'
              }
            >
              {message}
            </p>
          ) : null}

          <div className="venue-lists-accept-actions">
            {status === 'needs-auth' ? (
              <button
                type="button"
                className="venue-lists-accept-link-btn"
                onClick={() => navigate('/')}
              >
                Sign in
              </button>
            ) : null}
            {status === 'success' && listIdFromQuery ? (
              <button
                type="button"
                className="venue-lists-accept-link-btn"
                onClick={() => navigate(`/venue-lists/${listIdFromQuery}`)}
              >
                Open venue list
              </button>
            ) : null}
            <button
              type="button"
              className="venue-lists-accept-link-btn venue-lists-accept-link-btn--secondary"
              onClick={() => navigate('/venue-lists')}
            >
              Go to venue lists
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VenueListInviteAccept;
